import React, { useLayoutEffect, useRef } from 'react';
import { applyUvInsertTextEdit } from '@shared/view/uv-inserts/text-input-rules';
import { Label } from '../../Label/index.jsx';
import { FileInput } from '../../FileInput';
import fileInputStyles from '../../FileInput/file-input.module.css';
import inputStyles from '../personalisation-inputs.module.css';
import styles from './personalisation-uv-insert-slot.module.css';

/** Figma's placeholder for the text fields below the keepsake card. */
const FIELD_PLACEHOLDER = 'Your words';

/** The photo types and size the card's photo accepts, the same as the "Upload photo" button. */
const PHOTO_ACCEPT = 'png,jpg,jpeg,webp';
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/** The trimmed line item key of a face element, or '' when it has none. */
function elementKey(element) {
  return typeof element.lineItemPropertyKey === 'string' ? element.lineItemPropertyKey.trim() : '';
}

/**
 * The face elements on the active tab that ask for a field below the face: editable TEXT and
 * IMAGE_UPLOAD elements whose config sets `inputLabel`, in config order. Every other element
 * (and every other insert, none of which sets `inputLabel`) keeps today's behaviour.
 *
 * @param {Array<{ elementConfig: Array<Object> }>} facesToShow - The view model's faces for the active tab.
 * @returns {Array<Object>} The elements to render fields for.
 */
export function getFieldElements(facesToShow) {
  return facesToShow.flatMap((face) =>
    face.elementConfig.filter((element) => {
      if (typeof element.inputLabel !== 'string' || element.inputLabel.trim() === '' || !elementKey(element)) {
        return false;
      }
      if (element.type === 'IMAGE_UPLOAD') return true;
      return element.type === 'TEXT' && element.editable !== false;
    })
  );
}

/**
 * A textarea that is as tall as its text, wrapped lines included (`rows` only counts line breaks, so
 * a long message with none would scroll inside a one-line box). `min-height` in the CSS is the floor.
 */
function UvInsertTextField({ value, ...props }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight leaves out the border, which box-sizing: border-box height needs.
    const borderY = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + borderY}px`;
  }, [value]);

  return <textarea ref={ref} rows={1} value={value} {...props} />;
}

/**
 * Labelled fields below an insert's face, for the elements whose config asks for one (the Story of
 * Us keepsake card: its photo, the line under it and the message beside it, ALBAU-907).
 *
 * Each field is bound to the same view-model state as the face, so typing here updates the card
 * and tapping the card updates the field. The text fields are textareas so a line break typed on
 * the face is never lost; they start one line tall, as in the Figma, and grow to fit what is typed.
 *
 * @param {Object} props
 * @param {ReturnType<typeof import('./PersonalisationUvInsertSlot.vm').usePersonalisationUvInsertSlotViewModel>} props.vm
 * @param {Object} props.slot
 * @param {string} props.cropFailedMessage - Shown under the photo field when its crop failed.
 * @returns {import('react').JSX.Element|null}
 */
export function UvInsertFields({ vm, slot, cropFailedMessage }) {
  const fields = getFieldElements(vm.facesToShow);
  if (fields.length === 0) return null;

  return (
    <div className={styles['personalisation__uv-insert-fields']}>
      {fields.map((element) => {
        const key = elementKey(element);
        const id = `uv-insert-field-${slot.id}-${key}`;
        const labelId = `${id}-label`;

        if (element.type === 'IMAGE_UPLOAD') {
          return (
            // FileInput's hidden input has its own aria-label, which beats the <label>, so the group
            // carries the field's name for screen readers.
            <div
              key={key}
              className={styles['personalisation__uv-insert-field']}
              role="group"
              aria-labelledby={labelId}
            >
              <div id={labelId}>
                <Label required={element.required === true} htmlFor={id}>
                  {element.inputLabel}
                </Label>
              </div>
              <FileInput
                id={id}
                label="Choose File"
                accept={PHOTO_ACCEPT}
                maxFileSize={PHOTO_MAX_BYTES}
                buttonVariant="primary"
                buttonColor="orange"
                buttonSize="small"
                selectedFile={vm.imageByKey[key] ?? null}
                onFileSelect={(value) => {
                  const file =
                    value.userImage !== null && value.userImage.file instanceof File ? value.userImage.file : null;
                  vm.setImageByKey((prev) => ({ ...prev, [key]: file }));
                }}
              />
              {vm.cropFailedKeys.includes(key) && (
                <div className={fileInputStyles['file-input__error'] + ' body-sm font-medium'} role="alert">
                  {cropFailedMessage}
                </div>
              )}
            </div>
          );
        }

        const value = vm.textByKey[key] ?? '';
        const countId = `${id}-count`;

        return (
          <div key={key} className={styles['personalisation__uv-insert-field']}>
            <label className="field__label" htmlFor={id}>
              {element.inputLabel}
            </label>
            <div className={inputStyles['personalisation__input-wrapper']}>
              <UvInsertTextField
                id={id}
                className={'field__input ' + styles['personalisation__uv-insert-textarea']}
                placeholder={FIELD_PLACEHOLDER}
                value={value}
                aria-describedby={countId}
                autoComplete="off"
                spellCheck="false"
                onChange={(event) => {
                  const target = event.target;
                  const raw = target.value;
                  const next = applyUvInsertTextEdit({
                    previous: value,
                    raw,
                    maxLength: element.maxLength,
                    maxLines: element.maxLines,
                    maxCharsPerLine: element.maxCharsPerLine
                  });
                  if (next === value && raw !== value) {
                    // A rejected mid-message edit: React puts the old value back and the caret jumps to the
                    // end, so put it back where the customer was typing.
                    const caret = Math.max(0, Math.min(value.length, target.selectionStart - (raw.length - value.length)));
                    requestAnimationFrame(() => target.setSelectionRange(caret, caret));
                  }
                  vm.setText(key, next);
                }}
              />
              <p
                id={countId}
                className={
                  inputStyles['personalisation__input-length'] +
                  ' ' +
                  styles['personalisation__uv-insert-count'] +
                  ' body-xs'
                }
              >
                {value.length}/{element.maxLength}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
